import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { awaitJSON, DagLambdaFetch } from '@src/utils/fetch-utils';
import { useSetError } from '@src/redux/modals';

interface DagGraph {
  name: string;
}

// --- Server reads (React Query) ---------------------------------------------------------------

// The list of dag names is shared by every dag-name picker on the page. Each dag graph is
// effectively a company's pipeline, though a few dags are non-company variants like
// `mobius-{company}`.
export const useDagNames = (): ReturnType<typeof useQuery<string[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['dagNames'],
    queryFn: async (): Promise<string[]> => {
      const res = await DagLambdaFetch('/dag_graphs');
      const dags = await awaitJSON<DagGraph[]>(res);
      return dags.map(dag => dag.name).sort();
    }
  });

interface DagTask {
  task: string;
  task_name: string;
}

export interface DagTasks {
  tasks: string[];
  taskNames: string[];
}

// `task` is the generic step type shared across every dag (e.g. `WAITKPI`); `task_name` is that
// step's dag-specific instance (e.g. `WAITKPI-nike`). Both can be used as a restart point.
export const useDagTasks = (dagName: string): ReturnType<typeof useQuery<DagTasks>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['dagTasks', dagName],
    enabled: Boolean(dagName),
    queryFn: async (): Promise<DagTasks> => {
      const res = await DagLambdaFetch('/get_tasks_from_dag_name', { params: { dagName } });
      const dagTasks = await awaitJSON<DagTask[]>(res);
      // dag_tasks can have more than one enabled row for the same task/task_name (e.g. a task
      // reused across branches) -- dedupe so the dropdowns don't show repeated entries.
      return {
        tasks: [...new Set(dagTasks.map(task => task.task))].sort(),
        taskNames: [...new Set(dagTasks.map(task => task.task_name))].sort()
      };
    }
  });

// Both StartDagSection and StartSingleJobSection need the same tasks-for-this-dag query and the
// same "couldn't load tasks" modal on failure -- own the error surfacing here so it's not
// duplicated at both call sites.
export const useDagTasksWithErrorModal = (dagName: string): ReturnType<typeof useDagTasks> => {
  const setError = useSetError();
  const query = useDagTasks(dagName);
  const { error } = query;

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load tasks",
        message: `Failed to load tasks for this dag: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return query;
};

// --- Imperative writes (plain helpers) -------------------------------------------------------

export interface DagStartResult {
  success: boolean;
  message: string;
}

export interface StartDagParams {
  dag: string;
  start_task: string;
  start_task_name: string;
  override: boolean;
  retries: number;
}

export const startDag = async (params: StartDagParams): Promise<DagStartResult> => {
  const res = await DagLambdaFetch<StartDagParams>('/start', { method: 'POST', body: params });
  return awaitJSON<DagStartResult>(res);
};

export interface StartSingleJobParams {
  dag: string;
  task_name: string;
  select: string;
  exclude: string;
  override: boolean;
  retries: number;
}

export const startSingleJob = async (params: StartSingleJobParams): Promise<DagStartResult> => {
  const res = await DagLambdaFetch<StartSingleJobParams>('/start_single_job', {
    method: 'POST',
    body: params
  });
  return awaitJSON<DagStartResult>(res);
};
